/**
 * What still needs you, and by when.
 *
 * One focal figure - the number of documents waiting - because that is the only thing the person
 * can act on right now. The VAT deadline sits next to it, everything else is demoted to a quiet
 * row of figures.
 */
import { Plus, TriangleAlert, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { BelegNumber, KindBadge, StatusLabel } from "@/components/documents/document-badges";
import { EmptyState } from "@/components/shared/empty-state";
import { Stat } from "@/components/shared/stat";
import { UploadDialog } from "@/components/documents/upload-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAccounts, useTransactions } from "@/hooks/use-banking";
import { useAllDocuments } from "@/hooks/use-documents";
import { receiptGaps } from "@/lib/api/banking";
import { needsReview } from "@/lib/api/documents";
import { daysUntil, openFilingPeriod, uvaDueDate } from "@/lib/at/periods";
import { computeUva, smallBusinessUsage } from "@/lib/at/uva";
import { formatDate, formatMoney, formatPercent } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useWorkspace } from "@/lib/session/workspace-context";

const TODAY = () => new Date().toISOString().slice(0, 10);

export function DashboardPage() {
  const { t } = useTranslation();
  const { workspace, canWrite } = useWorkspace();
  const workspaceId = workspace?.id;
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: documents, isPending } = useAllDocuments(workspaceId);
  const { data: accounts } = useAccounts(workspaceId);
  const { data: transactions } = useTransactions(workspaceId);

  const today = TODAY();
  const period = useMemo(
    () => openFilingPeriod(today, workspace?.vatPeriod ?? "quarterly"),
    [today, workspace?.vatPeriod],
  );

  const open = (documents ?? []).filter(needsReview);
  const uva = useMemo(
    () => computeUva(documents ?? [], period, workspace?.accountingBasis ?? "cash"),
    [documents, period, workspace?.accountingBasis],
  );
  const gaps = receiptGaps(accounts ?? [], transactions ?? []);
  const missingReceipts = gaps.reduce((sum, gap) => sum + gap.missingCount, 0);
  const unpaidInvoices = (documents ?? []).filter(
    (document) => document.kind === "AR" && document.paymentStatus === "open",
  );
  const unpaidTotal =
    Math.round(
      unpaidInvoices.reduce((sum, document) => sum + (document.grossTotal ?? 0), 0) * 100,
    ) / 100;

  const dueDate = uvaDueDate(period);
  const daysLeft = daysUntil(dueDate, today);
  const smallBusiness =
    workspace?.vatScheme === "small_business"
      ? smallBusinessUsage(
          documents ?? [],
          Number(today.slice(0, 4)),
          workspace.smallBusinessLimit || 55000,
          workspace.accountingBasis,
        )
      : null;

  if (isPending) return <Skeleton className="h-96 w-full" aria-busy="true" />;

  return (
    <div className="space-y-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* A business name can be one long word ("…gesellschaft"): it wraps, never widens the page. */}
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight wrap-anywhere">{workspace?.name}</h1>
          <p className="text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
        </div>
        {canWrite && (
          <Button onClick={() => setUploadOpen(true)} data-testid="upload-receipt">
            <Upload /> {t("dashboard.upload")}
          </Button>
        )}
      </div>

      {/* The focal pair: what is waiting, and when it has to be filed. */}
      <section className="grid gap-6 rounded-lg border bg-card p-6 sm:grid-cols-2">
        <Stat
          emphasis
          tone={open.length > 0 ? "warning" : "success"}
          label={t("dashboard.openItems")}
          value={open.length}
          testId="open-items"
          hint={
            open.length > 0 ? (
              <Link
                to="/documents?status=open"
                className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
              >
                {t("dashboard.openItemsHint")}
              </Link>
            ) : (
              t("dashboard.allClear")
            )
          }
        />
        {workspace?.vatPeriod === "none" ? (
          <Stat
            label={t("dashboard.vatScheme")}
            value={t("vatScheme.small_business")}
            hint={t("dashboard.noUva")}
          />
        ) : (
          <Stat
            emphasis
            tone={daysLeft < 0 ? "warning" : undefined}
            label={t("dashboard.uvaDue", { period: period.key })}
            value={formatDate(dueDate)}
            testId="uva-due"
            hint={
              daysLeft >= 0
                ? t("dashboard.daysLeft", { count: daysLeft })
                : t("dashboard.overdue", { count: Math.abs(daysLeft) })
            }
          />
        )}
      </section>

      {smallBusiness && (
        <section className="space-y-2 rounded-lg border p-4" data-testid="small-business-limit">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-medium">{t("dashboard.smallBusinessTitle")}</p>
            <p className="text-sm text-muted-foreground num">
              {formatMoney(smallBusiness.turnover)} / {formatMoney(smallBusiness.limit)}
            </p>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                "h-full rounded-full",
                smallBusiness.share > 0.85 ? "bg-warning" : "bg-success",
              )}
              style={{ width: `${Math.min(smallBusiness.share, 1) * 100}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("dashboard.smallBusinessHint", { share: formatPercent(smallBusiness.share) })}
          </p>
        </section>
      )}

      <section className="grid gap-6 sm:grid-cols-3">
        <Stat
          label={t("dashboard.missingReceipts")}
          value={missingReceipts}
          tone={missingReceipts > 0 ? "warning" : undefined}
          testId="missing-receipts"
          hint={
            <Link
              to="/banking"
              className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
            >
              {t("dashboard.toBanking")}
            </Link>
          }
        />
        <Stat
          label={t("dashboard.unpaid")}
          value={formatMoney(unpaidTotal)}
          hint={t("dashboard.unpaidHint", { count: unpaidInvoices.length })}
        />
        <Stat
          label={uva.payable >= 0 ? t("dashboard.vatPayable") : t("dashboard.vatCredit")}
          value={formatMoney(Math.abs(uva.payable))}
          testId="vat-payable"
          hint={
            <Link
              to="/reports"
              className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
            >
              {t("dashboard.toReports")}
            </Link>
          }
        />
      </section>

      {uva.pendingCount > 0 && workspace?.vatPeriod !== "none" && (
        <Alert data-testid="pending-warning">
          <TriangleAlert className="size-4" />
          <AlertTitle>{t("dashboard.pendingTitle", { count: uva.pendingCount })}</AlertTitle>
          <AlertDescription>{t("dashboard.pendingText")}</AlertDescription>
        </Alert>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">{t("dashboard.needsAttention")}</h2>
          <Button asChild variant="ghost" size="sm">
            <Link to="/documents">{t("common.viewAll")}</Link>
          </Button>
        </div>
        {open.length === 0 ? (
          <EmptyState
            title={t("dashboard.nothingOpen")}
            description={t("dashboard.nothingOpenHint")}
            testId="nothing-open"
            action={
              canWrite && (
                <Button variant="outline" size="sm" onClick={() => setUploadOpen(true)}>
                  <Plus /> {t("dashboard.upload")}
                </Button>
              )
            }
          />
        ) : (
          <ul className="divide-y rounded-lg border bg-card">
            {open.slice(0, 6).map((document) => (
              <li key={document.id}>
                <Link
                  to={`/documents/${document.id}`}
                  className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/50"
                  data-testid="attention-row"
                >
                  {/* The Belegnummer already starts with the kind; a phone needs the room. */}
                  <KindBadge kind={document.kind} className="hidden sm:inline-flex" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {document.partnerName || t("document.noPartner")}
                    </p>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                      <BelegNumber value={document.docNumber} />
                      <span className="whitespace-nowrap">{formatDate(document.issueDate)}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <span className="text-sm num">
                      {formatMoney(document.grossTotal, document.currency)}
                    </span>
                    <StatusLabel status={document.status} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}
